import { createFileRoute, Link } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { CheckCircle } from "lucide-react";
import { useState } from "react";

import { usePasswordResetRequestMutation } from "@/api/auth/usePasswordResetRequestMutation";
import { Logo } from "@/components/brand/Logo";
import toast from "@/components/feedback/toast";
import { BackgroundVideoPlaylist } from "@/components/layout/BackgroundVideoPlaylist";

export const Route = createFileRoute("/forgot-password")({
  component: RouteComponent,
});

function RouteComponent() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const resetRequest = usePasswordResetRequestMutation();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await resetRequest.mutateAsync(email);
      setSent(true);
    } catch {
      toast({ message: "Operacja nie powiodła się.", type: "error" });
    }
  };

  return (
    <div className="h-dvh relative overflow-hidden bg-black">
      <BackgroundVideoPlaylist
        sources={[
          "https://test-video-ai.b-cdn.net/videos/b4d9606a-f396-43df-9c59-9d3599c32b66.mp4",
        ]}
        darkOverlay={false}
      />

      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent pointer-events-none" />
      <div className="absolute inset-0 bg-gradient-to-r from-black/40 to-transparent pointer-events-none" />

      <div className="relative z-10 h-full flex flex-col justify-end md:justify-center px-6 pb-10 md:pb-0">
        <div className="w-full max-w-sm mx-auto md:mx-0 md:ml-[10%] lg:ml-[15%]">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="mb-8"
          >
            <div className="mb-3">
              <Logo size="lg" className="text-white/90" />
            </div>
            <p className="text-white/40 text-sm leading-relaxed">
              Resetowanie hasła
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15, ease: "easeOut" }}
          >
            <div className="bg-white/[0.07] backdrop-blur-2xl border border-white/[0.09] rounded-2xl p-6 shadow-2xl">
              {sent ? (
                <div className="text-center space-y-4 py-2">
                  <div className="flex justify-center">
                    <div className="size-12 rounded-full bg-green-500/10 flex items-center justify-center">
                      <CheckCircle className="size-6 text-green-400" />
                    </div>
                  </div>
                  <h2 className="text-base font-semibold text-white">
                    Sprawdź skrzynkę
                  </h2>
                  <p className="text-sm text-white/40 leading-relaxed">
                    Jeśli konto z adresem <span className="text-white/70">{email}</span> istnieje, wysłaliśmy link do resetowania hasła.
                  </p>
                  <Link
                    to="/login"
                    className="inline-block text-xs text-white/30 hover:text-white/60 transition-colors mt-2"
                  >
                    ← Wróć do logowania
                  </Link>
                </div>
              ) : (
                <form onSubmit={(e) => { void handleSubmit(e); }} className="space-y-4">
                  <p className="text-sm text-white/40 text-center">
                    Podaj adres email powiązany z kontem.
                  </p>
                  <div>
                    <label className="block text-[10px] font-medium uppercase tracking-[1.5px] text-white/30 mb-2">
                      Email
                    </label>
                    <input
                      type="email"
                      placeholder="ty@email.com"
                      value={email}
                      onChange={(e) => { setEmail(e.target.value); }}
                      required
                      className="w-full bg-white/[0.06] border border-white/[0.08] rounded-xl px-4 py-2.5 text-sm text-white placeholder:text-white/25 outline-none focus:ring-2 focus:ring-accent/50 focus:border-transparent transition-all"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={resetRequest.isPending || !email}
                    className="w-full bg-accent rounded-xl py-2.5 text-sm font-medium text-white shadow-glow hover:shadow-glow-strong hover:brightness-110 disabled:opacity-50 disabled:shadow-none transition-all cursor-pointer"
                  >
                    {resetRequest.isPending ? "Wysyłanie..." : "Wyślij link resetujący"}
                  </button>
                  <div className="text-center">
                    <Link
                      to="/login"
                      className="text-xs text-white/30 hover:text-white/60 transition-colors"
                    >
                      ← Wróć do logowania
                    </Link>
                  </div>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
